import React from 'react';

import { Link } from 'react-router-dom';

import AppBar from '@material-ui/core/AppBar';
import Hidden from '@material-ui/core/Hidden';
import { makeStyles } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';

// A route is typically required for a menu tab, but there are a few exceptions:
// Cello custom launcher does not have a route. The cello.boot command will open the Cello modal
// from one of our elements when configured with a custom selector. We pass that value as the customAttribute field
// Starting Userflow tour does not have a route, as we just initialize the flow on click
export type MenuTabOptions =
  | { route: string; customAttribute?: never }
  | { route?: never; customAttribute: Record<string, string> }
  | { route?: never; customAttribute?: never };

export type MenuTab = {
  key: string;
  label: string;
  iconName?: string;
  hasPermissions: boolean;
  orgId?: number;
  jobId?: number | null;
  children?: React.ReactElement;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
  useTooltip?: boolean;
  whitelistedQueryParams?: string[];
  previousLocationPath?: string;
} & MenuTabOptions;

type DesktopTabProps = {
  label: string;
  jobId?: number | null;
  orgId?: number;
  onClick?: React.MouseEventHandler<any>;
  className?: string;
  disabled?: boolean;
  useTooltip?: boolean;
} & MenuTabOptions;

type InputProps = {
  variant: 'collapsible' | 'permanent';
  getSelectedTab: () => number;
  DesktopComponent:
    | React.FunctionComponent<DesktopTabProps>
    | React.ComponentClass<DesktopTabProps>;
  navigationComponent?: React.ReactElement;
  mainTabs: Array<MenuTab>;
  lowerTabs?: Array<MenuTab>; // This is for the custom tab grouping like Company Settings and Admin Panel on the dashboard
};

const useStyles = makeStyles(() => ({
  permanentNavContainer: {
    minWidth: '240px',
  },
}));

export const UiIconNav = ({
  getSelectedTab,
  DesktopComponent,
  navigationComponent,
  mainTabs,
  lowerTabs,
  variant,
}: InputProps) => {
  const classes = useStyles();

  const renderDesktopNav = () => (
    <div
      className={`leftNav-container ${
        variant === 'permanent' ? classes.permanentNavContainer : ''
      }`}
      data-cy="navbar"
    >
      <div
        className={`leftNav ${
          variant === 'permanent' ? 'u-width-100 u-padding-top' : 'leftNav-collapsible'
        }`}
      >
        <div className="u-display-flex u-flexDirection-column u-whiteSpace-nowrap">
          {navigationComponent && <div className="u-margin-bottom">{navigationComponent}</div>}
          {mainTabs?.map(
            (tab) =>
              tab.hasPermissions && <DesktopComponent {...tab}>{tab.children}</DesktopComponent>,
          )}
        </div>
        <div className="u-display-flex u-flexDirection-column">
          {lowerTabs?.map(
            (tab) =>
              tab.hasPermissions && <DesktopComponent {...tab}>{tab.children}</DesktopComponent>,
          )}
        </div>
      </div>
    </div>
  );

  const renderMobileNav = () => (
    <>
      <AppBar position="static" color="default" className="u-height-fitContent">
        <Tabs variant="scrollable" value={getSelectedTab()}>
          {mainTabs?.map((tab) => {
            if (!tab.route) {
              throw new Error('Mobile tabs need a route');
            }
            return (
              tab.hasPermissions &&
              !tab.disabled && (
                <Tab
                  key={tab.key}
                  label={tab.label}
                  to={tab.route}
                  className={tab.className}
                  component={Link}
                />
              )
            );
          })}
        </Tabs>
      </AppBar>
      <div className="u-border-bottom">{navigationComponent || null}</div>
    </>
  );

  return (
    <>
      <Hidden key="desktop" smDown>
        {renderDesktopNav()}
      </Hidden>
      <Hidden key="mobile" mdUp>
        {renderMobileNav()}
      </Hidden>
    </>
  );
};
